import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { AvisoDeFalha } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { Campo, CLASSE_DE_CAMPO } from '@/features/changes/componentes';
import { chavesDeApoio, obterOpcoesDeFormulario } from '@/features/tickets/lookups.api';
import {
  chavesDeConhecimento,
  criarArtigo,
  editarArtigo,
  obterArtigo,
} from '@/features/knowledge/conhecimento.api';
import { CorpoDoArtigo } from '@/features/knowledge/componentes';
import { CAMINHO_ARTIGO, LinkDaLista, useIrPara } from '@/features/knowledge/navegacao';
import {
  ALCANCE_DE_VISIBILIDADE,
  ROTULO_DE_VISIBILIDADE,
  VISIBILIDADES,
  type Visibilidade,
} from '@/features/knowledge/tipos';

/** O que o formulário guarda. Espelha o DTO, sem o que o servidor decide. */
interface Rascunho {
  title: string;
  summary: string;
  bodyHtml: string;
  keywords: string;
  visibility: Visibilidade;
  categoryId: string;
  reviewIntervalDays: number;
}

const VAZIO: Rascunho = {
  title: '',
  summary: '',
  bodyHtml: '',
  keywords: '',
  visibility: 'INTERNAL',
  categoryId: '',
  reviewIntervalDays: 180,
};

/**
 * O editor de artigo.
 *
 * ## Por que o campo do corpo é HTML, e não um editor rico
 *
 * Foi uma escolha, não uma pendência. Um editor WYSIWYG de verdade é uma
 * dependência grande, e o que ele resolve — negrito, lista, link — o operador
 * já resolve com um punhado de tags. O que o sistema **não** pode delegar ao
 * editor é a segurança: o servidor higieniza o HTML na escrita, com lista de
 * permissão, e é essa higienização que torna o corpo exibível. Trocar este
 * campo por um editor rico depois não muda nada do lado do servidor.
 *
 * A pré-visualização ao lado usa o mesmo componente da ficha — o que se vê
 * aqui é o que vai aparecer lá, tirando o que a higienização remover.
 *
 * ## O aviso de público
 *
 * O seletor de visibilidade mostra **quem alcança**, e não o nome da opção.
 * Escolher "Cliente" num procedimento que cita senha de firewall é o erro mais
 * caro desta fase, e ele se evita dizendo o que a escolha faz.
 */
export function ArtigoEditorPage({ artigoId }: { artigoId?: string }) {
  const editando = artigoId !== undefined;
  const cliente = useQueryClient();
  const navegar = useIrPara();
  const [falha, setFalha] = useState<Falha | null>(null);
  const [rascunho, setRascunho] = useState<Rascunho | null>(editando ? null : VAZIO);
  const [notaDeMudanca, setNotaDeMudanca] = useState('');

  const existente = useQuery({
    queryKey: chavesDeConhecimento.artigo(artigoId ?? ''),
    queryFn: () => obterArtigo(artigoId as string),
    enabled: editando,
  });

  // As mesmas categorias do chamado, da mesma rota de apoio: o artigo usa a
  // classificação existente, e não um catálogo próprio.
  const apoio = useQuery({
    queryKey: chavesDeApoio.formulario,
    queryFn: obterOpcoesDeFormulario,
    staleTime: 5 * 60_000,
  });

  // Semeia o formulário uma vez, quando o artigo chega. Um `useEffect` que
  // copiasse a cada renderização desfaria o que a pessoa está digitando.
  const carregado = existente.data;
  if (editando && carregado && rascunho === null) {
    setRascunho({
      title: carregado.title,
      summary: carregado.summary,
      bodyHtml: carregado.bodyHtml,
      keywords: carregado.keywords.join(', '),
      visibility: carregado.visibility,
      categoryId: carregado.categoryId ?? '',
      reviewIntervalDays: carregado.reviewIntervalDays,
    });
  }

  const palavras = useMemo(
    () =>
      (rascunho?.keywords ?? '')
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k !== ''),
    [rascunho?.keywords],
  );

  const salvar = useMutation({
    mutationFn: async () => {
      const r = rascunho as Rascunho;
      const corpo = {
        title: r.title.trim(),
        summary: r.summary.trim(),
        bodyHtml: r.bodyHtml,
        keywords: palavras,
        visibility: r.visibility,
        reviewIntervalDays: r.reviewIntervalDays,
        ...(r.categoryId !== '' ? { categoryId: r.categoryId } : {}),
      };
      return editando
        ? editarArtigo(artigoId as string, {
            ...corpo,
            ...(notaDeMudanca.trim() !== '' ? { changeNote: notaDeMudanca.trim() } : {}),
            version: carregado?.version,
          })
        : criarArtigo(corpo);
    },
    onSuccess: (artigo) => {
      setFalha(null);
      void cliente.invalidateQueries({ queryKey: chavesDeConhecimento.raiz });
      navegar({ to: CAMINHO_ARTIGO, params: { artigoId: artigo.id } });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  if (editando && rascunho === null) {
    return (
      <SkeletonRegion label="Carregando artigo" className="mx-auto w-full max-w-5xl px-4 py-6">
        <Skeleton className="h-96 w-full rounded-lg" />
      </SkeletonRegion>
    );
  }
  const r = rascunho as Rascunho;
  const definir = <K extends keyof Rascunho>(campo: K, valor: Rascunho[K]) =>
    setRascunho({ ...r, [campo]: valor });

  const completo =
    r.title.trim().length >= 3 && r.summary.trim().length >= 10 && r.bodyHtml.trim() !== '';

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 sm:px-6">
      <LinkDaLista className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Base de conhecimento
      </LinkDaLista>

      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {editando ? `Editar ${carregado?.code ?? ''}` : 'Novo artigo'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {editando
            ? 'Cada alteração de texto congela uma versão no histórico. Nada é sobrescrito.'
            : 'O artigo nasce em rascunho e vai ao ar depois de revisão e aprovação.'}
        </p>
      </header>

      {falha && <AvisoDeFalha falha={falha} />}
      {existente.isError && <AvisoDeFalha falha={classificarFalha(existente.error)} />}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <Campo rotulo="Título" htmlFor="titulo" dica="Como a pessoa vai reconhecer o artigo na lista de resultados.">
            <input
              id="titulo"
              value={r.title}
              onChange={(e) => definir('title', e.target.value)}
              className={CLASSE_DE_CAMPO}
              maxLength={300}
            />
          </Campo>

          <Campo
            rotulo="Resumo"
            htmlFor="resumo"
            dica="Três linhas, no máximo. É o que aparece na busca e na sugestão dentro do chamado — e é por ele que se decide abrir ou não."
          >
            <textarea
              id="resumo"
              value={r.summary}
              onChange={(e) => definir('summary', e.target.value)}
              rows={3}
              className={CLASSE_DE_CAMPO}
              maxLength={2000}
            />
          </Campo>

          <Campo
            rotulo="Corpo (HTML)"
            htmlFor="corpo"
            dica="Aceita p, ul, ol, h2, h3, code, pre, table, a e img. O servidor remove o resto na gravação — script e atributo de evento nunca chegam ao banco."
          >
            <textarea
              id="corpo"
              value={r.bodyHtml}
              onChange={(e) => definir('bodyHtml', e.target.value)}
              rows={16}
              className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
            />
          </Campo>

          <Campo
            rotulo="Palavras-chave"
            htmlFor="palavras"
            dica="Separadas por vírgula. Sinônimos e siglas que o texto NÃO usa — entram na busca com o peso do título."
          >
            <input
              id="palavras"
              value={r.keywords}
              onChange={(e) => definir('keywords', e.target.value)}
              className={CLASSE_DE_CAMPO}
              placeholder="spooler, fila, PDF"
            />
          </Campo>
        </div>

        <div className="space-y-4">
          <Campo rotulo="Quem pode ver" htmlFor="visibilidade" dica={ALCANCE_DE_VISIBILIDADE[r.visibility]}>
            <select
              id="visibilidade"
              value={r.visibility}
              onChange={(e) => definir('visibility', e.target.value as Visibilidade)}
              className={CLASSE_DE_CAMPO}
            >
              {VISIBILIDADES.map((v) => (
                <option key={v} value={v}>
                  {ROTULO_DE_VISIBILIDADE[v]}
                </option>
              ))}
            </select>
          </Campo>

          {r.visibility !== 'INTERNAL' && (
            <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
              Depois de publicado, este artigo sai da Aptum. Confira se o texto não cita senha,
              endereço interno, nome de servidor ou dado de outro cliente.
            </p>
          )}

          <Campo rotulo="Categoria" htmlFor="categoria" dica="A mesma classificação do chamado. É por ela que o artigo é oferecido durante o atendimento.">
            <select
              id="categoria"
              value={r.categoryId}
              onChange={(e) => definir('categoryId', e.target.value)}
              className={CLASSE_DE_CAMPO}
            >
              <option value="">Sem categoria</option>
              {(apoio.data?.categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Campo>

          <Campo
            rotulo="Revisar a cada (dias)"
            htmlFor="revisao"
            dica="Conhecimento sem validade vira folclore. Um procedimento de firewall envelhece mais rápido que 'como pedir crachá'."
          >
            <input
              id="revisao"
              type="number"
              min={1}
              max={3650}
              value={r.reviewIntervalDays}
              onChange={(e) => definir('reviewIntervalDays', Number(e.target.value) || 180)}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>

          {editando && (
            <Campo
              rotulo="O que mudou"
              htmlFor="nota"
              dica="Vai para o histórico. Sem isto, a lista de versões é uma pilha de textos parecidos."
            >
              <input
                id="nota"
                value={notaDeMudanca}
                onChange={(e) => setNotaDeMudanca(e.target.value)}
                className={CLASSE_DE_CAMPO}
                maxLength={2000}
              />
            </Campo>
          )}

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-foreground">Pré-visualização</h2>
            <Card>
              <CardBody>
                {r.bodyHtml.trim() === '' ? (
                  <p className="text-sm text-muted-foreground">
                    O corpo ainda está vazio.
                  </p>
                ) : (
                  <CorpoDoArtigo html={r.bodyHtml} />
                )}
              </CardBody>
            </Card>
            <p className="text-xs text-muted-foreground">
              O que a higienização do servidor remover não vai aparecer na ficha, mesmo que
              apareça aqui.
            </p>
          </section>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => window.history.back()}>
          Cancelar
        </Button>
        <Button onClick={() => salvar.mutate()} disabled={!completo} loading={salvar.isPending}>
          <Save className="mr-1.5 h-4 w-4" aria-hidden />
          {editando ? 'Salvar nova versão' : 'Criar rascunho'}
        </Button>
      </div>
    </div>
  );
}
